import { TextField } from "@mui/material";
import { useRef, useState } from "react";
import { Controller } from "react-hook-form";
import InputMask from "react-input-mask";

export default function RHFBirthDateInput({
    name,
    control,
    label,
    errors,
    onFocus,
    onBlur,
}: any) {
    const [isFocused, setIsFocused] = useState(false);
    const inputElementRef = useRef<HTMLInputElement | null>(null);
    const cursorPositionRef = useRef<number | null>(null);

    const handleFocus = () => setIsFocused(true);

    const handleBlur = (e: any) => {
        if (!e.target.value) {
            setIsFocused(false);
        }
    };

    const isValidDate = (day: number, month: number, year: number): boolean => {
        // Check basic ranges
        if (month < 1 || month > 12) return false;
        if (day < 1) return false;

        // Days in each month (non-leap year)
        const daysInMonth = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

        // Check for leap year
        const isLeapYear = (year % 4 === 0 && year % 100 !== 0) || (year % 400 === 0);
        if (isLeapYear && month === 2) {
            return day <= 29;
        }

        return day <= daysInMonth[month - 1];
    };

    const validateBirthDate = (value: string) => {
        if (!value || value.includes("_")) {
            return true; // Let required validation handle empty values
        }

        // Parse the date string (DD/MM/YYYY)
        const parts = value.split("/");
        if (parts.length !== 3) return false;

        const day = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10);
        const year = parseInt(parts[2], 10);

        // Basic validation
        if (isNaN(day) || isNaN(month) || isNaN(year)) return false;

        // Validate day is valid for the month and year
        if (!isValidDate(day, month, year)) {
            return false;
        }

        // Create date object and check it's not today or future
        const inputDate = new Date(year, month - 1, day);
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        // Check if date is today or in the future
        if (inputDate >= today) {
            return false;
        }

        return true;
    };

    const handleDateInput = (newValue: string, oldValue: string): string => {
        // Remove the mask characters to get just the numbers
        const cleanNew = newValue.replace(/[^0-9]/g, '');
        const cleanOld = oldValue.replace(/[^0-9]/g, '');

        // If user is deleting, allow it
        if (cleanNew.length < cleanOld.length) {
            return newValue;
        }

        // Day validation (first 2 digits)
        if (cleanNew.length >= 1) {
            const firstDigit = parseInt(cleanNew[0], 10);
            // First digit of day can only be 0-3
            if (firstDigit > 3) {
                return oldValue;
            }
        }

        if (cleanNew.length >= 2) {
            const day = parseInt(cleanNew.substring(0, 2), 10);
            // Day must be 01-31
            if (day === 0 || day > 31) {
                return oldValue;
            }
        }

        // Month validation (digits 3-4)
        if (cleanNew.length >= 3) {
            const firstMonthDigit = parseInt(cleanNew[2], 10);
            // First digit of month can only be 0 or 1
            if (firstMonthDigit > 1) {
                return oldValue;
            }
        }

        if (cleanNew.length >= 4) {
            const month = parseInt(cleanNew.substring(2, 4), 10);
            // Month must be 01-12
            if (month === 0 || month > 12) {
                return oldValue;
            }

            // Check if day is valid for the entered month
            const day = parseInt(cleanNew.substring(0, 2), 10);
            const currentYear = new Date().getFullYear();
            if (!isValidDate(day, month, currentYear)) {
                return oldValue;
            }
        }

        // Year validation (digits 5-8)
        const currentYear = new Date().getFullYear();
        const currentMonth = new Date().getMonth() + 1;
        const currentDay = new Date().getDate();

        if (cleanNew.length >= 5) {
            const yearDigits = cleanNew.substring(4);

            // Check each digit of the year as it's being typed
            if (yearDigits.length === 1) {
                // First digit of year - check if it could lead to future year
                const firstYearDigit = parseInt(yearDigits[0], 10);
                const currentYearFirstDigit = parseInt(currentYear.toString()[0], 10);
                if (firstYearDigit > currentYearFirstDigit) {
                    return oldValue;
                }
            } else if (yearDigits.length === 2) {
                // First two digits of year
                const yearPrefix = parseInt(yearDigits, 10);
                const currentYearPrefix = parseInt(currentYear.toString().substring(0, 2), 10);
                if (yearPrefix > currentYearPrefix) {
                    return oldValue;
                }
            } else if (yearDigits.length === 3) {
                // First three digits of year
                const yearPrefix = parseInt(yearDigits, 10);
                const currentYearPrefix = parseInt(currentYear.toString().substring(0, 3), 10);
                if (yearPrefix > currentYearPrefix) {
                    return oldValue;
                }
            } else if (yearDigits.length === 4) {
                // Complete year
                const year = parseInt(yearDigits, 10);
                if (year > currentYear) {
                    return oldValue;
                }

                // If same year, check if date is today or future
                if (year === currentYear) {
                    const day = parseInt(cleanNew.substring(0, 2), 10);
                    const month = parseInt(cleanNew.substring(2, 4), 10);

                    if (month > currentMonth) {
                        return oldValue;
                    }
                    if (month === currentMonth && day >= currentDay) {
                        return oldValue;
                    }
                }

                // Final validation with actual year (for leap year check)
                const day = parseInt(cleanNew.substring(0, 2), 10);
                const month = parseInt(cleanNew.substring(2, 4), 10);
                if (!isValidDate(day, month, year)) {
                    return oldValue;
                }
            }
        }

        return newValue;
    };

    return (
        <Controller
            name={name}
            control={control}
            rules={{
                validate: (value) => {
                    if (!validateBirthDate(value)) {
                        return "Geçerli bir doğum tarihi giriniz (geçmiş bir tarih olmalı)";
                    }
                    return true;
                },
            }}
            render={({ field: { onChange, onBlur: fieldOnBlur, value } }) => {
                return (
                    <InputMask
                        mask="99/99/9999"
                        value={value}
                        onBeforeInput={(e: any) => {
                            // Capture cursor position before input
                            if (inputElementRef.current) {
                                cursorPositionRef.current = inputElementRef.current.selectionStart;
                            }
                        }}
                        onChange={(e) => {
                            const validatedValue = handleDateInput(e.target.value, value || '');

                            // If the validated value is different from input, it means invalid character was entered
                            if (validatedValue !== e.target.value) {
                                onChange(validatedValue);

                                // Reset cursor position after state update
                                setTimeout(() => {
                                    if (inputElementRef.current && cursorPositionRef.current !== null) {
                                        inputElementRef.current.setSelectionRange(
                                            cursorPositionRef.current,
                                            cursorPositionRef.current
                                        );
                                    }
                                }, 0);
                            } else {
                                onChange(validatedValue);
                            }
                        }}
                        onBlur={(e) => {
                            setIsFocused(false);
                            fieldOnBlur();
                            handleBlur(e);
                            if (onBlur) onBlur();
                        }}
                        onFocus={() => {
                            setIsFocused(true);
                            handleFocus();
                            if (onFocus) onFocus();
                        }}
                    >
                        {/* @ts-ignore */}
                        {(props) => (
                            <TextField
                                {...props}
                                inputRef={inputElementRef}
                                name={name}
                                placeholder="GG/AA/YYYY"
                                InputProps={{ sx: { borderRadius: 2 } }}
                                helperText={
                                    !isFocused && errors?.[name]?.message
                                        ? errors?.[name]?.message
                                        : " "
                                }
                                error={!isFocused && !!errors?.[name]?.message}
                                type="text"
                                fullWidth
                                label={label}
                                inputProps={{ inputMode: "numeric" }}
                                FormHelperTextProps={{
                                    sx: {
                                        minHeight: "20px",
                                    },
                                }}
                            />
                        )}
                    </InputMask>
                );
            }}
        />
    );
}

